import { z } from 'zod';
import { SortQuerySchema } from '../common';
import {
  ESTADO_CUENTA_EXPORT_FORMAT,
  ESTADO_CUENTA_MOVEMENT_TYPE,
  ESTADO_CUENTA_SERIE_MENSUAL,
} from './types';
import {
  CLIENT_DEACTIVATION_CURRENT_PERIOD_TREATMENT as CURRENT_PERIOD_TREATMENT,
  CLIENT_DEACTIVATION_CUTOFF_STRATEGY as CUTOFF_STRATEGY,
} from './deactivacion';

/** Fecha ISO YYYY-MM-DD (sin hora). null limpia el campo en la API. */
const fechaISO = z
  .string()
  .regex(/^\d{4}-\d{2}-\d{2}$/, 'fecha inválida: use el formato YYYY-MM-DD');

const EstadoCuentaFiltersSchema = z.object({
  fechaDesde: fechaISO.optional(),
  fechaHasta: fechaISO.optional(),
  tipo: z
    .enum([
      ESTADO_CUENTA_MOVEMENT_TYPE.CARGO,
      ESTADO_CUENTA_MOVEMENT_TYPE.PAGO,
      ESTADO_CUENTA_MOVEMENT_TYPE.PREPAGO,
      ESTADO_CUENTA_MOVEMENT_TYPE.CREDITO_GENERADO,
      ESTADO_CUENTA_MOVEMENT_TYPE.CREDITO_APLICADO,
      ESTADO_CUENTA_MOVEMENT_TYPE.AJUSTE,
      ESTADO_CUENTA_MOVEMENT_TYPE.ANULACION,
      ESTADO_CUENTA_MOVEMENT_TYPE.REVERSO,
      ESTADO_CUENTA_MOVEMENT_TYPE.RECONCILIACION,
    ])
    .optional(),
  estado: z.string().trim().min(1).max(80).optional(),
  search: z.string().trim().max(120).optional(),
});

function hasValidEstadoCuentaDateRange(value: {
  fechaDesde?: string;
  fechaHasta?: string;
}): boolean {
  return (
    value.fechaDesde === undefined ||
    value.fechaHasta === undefined ||
    value.fechaDesde <= value.fechaHasta
  );
}

const ESTADO_CUENTA_DATE_RANGE_ERROR = {
  path: ['fechaHasta'],
  message: 'fechaHasta must be on or after fechaDesde',
};

/** Converts blank UI text values into an explicit null without changing nonblank values. */
export function blankStringToNull<T>(value: T): T | null {
  return typeof value === 'string' && value.trim() === '' ? null : value;
}

function optionalClientText(schema: z.ZodString) {
  return z.preprocess(blankStringToNull, schema.nullable().optional());
}

const latitudGps = z.coerce.number().finite().min(-90).max(90);
const longitudGps = z.coerce.number().finite().min(-180).max(180);

const clienteUbicacionCompleta = (
  latitud: number | null | undefined,
  longitud: number | null | undefined,
): boolean =>
  (latitud === undefined && longitud === undefined) ||
  (latitud === null && longitud === null) ||
  (latitud !== null && latitud !== undefined && longitud !== null && longitud !== undefined);

const validarClienteUbicacion = <T extends { latitud?: number | null; longitud?: number | null }>(
  value: T,
  ctx: z.RefinementCtx,
) => {
  if (!clienteUbicacionCompleta(value.latitud, value.longitud)) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['latitud'],
      message: 'latitud y longitud deben informarse juntas',
    });
    return;
  }

  if (value.latitud === 0 && value.longitud === 0) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['latitud'],
      message: 'las coordenadas GPS no pueden ser el sentinel 0,0',
    });
  }
};

export const CreateClienteDtoSchema = z
  .object({
    nombre: z.string().min(1),
    fechaNacimiento: fechaISO.nullable().optional(),
    identificacion: optionalClientText(z.string()),
    telefonoPrincipal: optionalClientText(z.string()),
    telefonoReferencia: optionalClientText(z.string()),
    email: optionalClientText(z.string().email()),
    ip: optionalClientText(z.string()),
    tipoCliente: z.coerce.number().int().optional(),
    tipoSuscripcion: z.coerce.number().int().positive().optional(),
    // El estado siempre va definido al crear (default de negocio: ACTIVO).
    estadoCliente: z.coerce.number().int().min(1),
    nit: optionalClientText(z.string()),
    direccion: optionalClientText(z.string()),
    comentarios: optionalClientText(z.string()),
    fechaCobro: fechaISO,
    diaCobro: z.coerce.number().int().min(1).max(31).optional(),
    fechaAlta: fechaISO.nullable().optional(),
    latitud: latitudGps.nullable().optional(),
    longitud: longitudGps.nullable().optional(),
  })
  .superRefine(validarClienteUbicacion);

export type CreateClienteDto = z.infer<typeof CreateClienteDtoSchema>;

export const UpdateClienteDtoSchema = z
  .object({
    nombre: z.string().optional(),
    fechaNacimiento: fechaISO.nullable().optional(),
    identificacion: optionalClientText(z.string()),
    telefonoPrincipal: optionalClientText(z.string()),
    telefonoReferencia: optionalClientText(z.string()),
    email: optionalClientText(z.string().email()),
    ip: optionalClientText(z.string()),
    tipoCliente: z.coerce.number().int().optional(),
    tipoSuscripcion: z.coerce.number().int().positive().optional(),
    estadoCliente: z.coerce.number().int().min(1).optional(),
    nit: optionalClientText(z.string()),
    direccion: optionalClientText(z.string()),
    comentarios: optionalClientText(z.string()),
    // fechaCobro NO es nullable: la columna es NOT NULL (no se puede limpiar).
    fechaCobro: fechaISO.optional(),
    diaCobro: z.coerce.number().int().min(1).max(31).optional(),
    fechaAlta: fechaISO.nullable().optional(),
    latitud: latitudGps.nullable().optional(),
    longitud: longitudGps.nullable().optional(),
  })
  .superRefine(validarClienteUbicacion);

export type UpdateClienteDto = z.infer<typeof UpdateClienteDtoSchema>;

export const PreviewClienteUbicacionDtoSchema = z
  .object({
    latitud: latitudGps,
    longitud: longitudGps,
  })
  .refine((value) => value.latitud !== 0 || value.longitud !== 0, {
    path: ['latitud'],
    message: 'GPS coordinates cannot be the 0,0 sentinel',
  });

export type PreviewClienteUbicacionDto = z.infer<typeof PreviewClienteUbicacionDtoSchema>;

export const QueryClientesDtoSchema = z
  .object({
    offset: z.coerce.number().int().min(0).default(0),
    limit: z.coerce.number().int().min(1).max(100).default(20),
    search: z.string().optional(),
    estado: z.coerce.number().int().optional(),
    tipo: z.coerce.number().int().optional(),
  })
  .merge(SortQuerySchema);

export type QueryClientesDto = z.infer<typeof QueryClientesDtoSchema>;

/**
 * Query-string booleans arrive as 'true'/'false' strings; coerce them before
 * the boolean check. Mirrors the queryBoolean convention of the catalogos
 * and auth DTOs.
 */
const booleanQuerySchema = z.preprocess((value: unknown) => {
  if (value === 'true') return true;
  if (value === 'false') return false;
  return value;
}, z.boolean());

/**
 * GET /admin/clientes/:id/servicios — per-client service listing query.
 *
 * `incluirBajas` defaults to `true`: BAJA services stay listed, which is the
 * existing behavior for every consumer. With `false`, ONLY the BAJA state is
 * excluded from the listing — BAJA_AL_CORTE remains visible. Precedence: when
 * an explicit `estado` filter is provided together with `incluirBajas=false`,
 * BAJA is still excluded (the filter cannot re-include BAJA).
 */
export const QueryServiciosPorClienteDtoSchema = z
  .object({
    offset: z.coerce.number().int().min(0).default(0),
    limit: z.coerce.number().int().min(1).max(100).default(20),
    estado: z.coerce.number().int().optional(),
    incluirBajas: booleanQuerySchema.default(true),
  })
  .merge(SortQuerySchema);

export type QueryServiciosPorClienteDto = z.infer<typeof QueryServiciosPorClienteDtoSchema>;

export const QueryEstadoCuentaDtoSchema = EstadoCuentaFiltersSchema.extend({
  offset: z.coerce.number().int().min(0).default(0),
  limit: z.coerce.number().int().min(1).max(100).default(20),
}).refine(hasValidEstadoCuentaDateRange, ESTADO_CUENTA_DATE_RANGE_ERROR);

export type QueryEstadoCuentaDto = z.infer<typeof QueryEstadoCuentaDtoSchema>;

/** Simple statement query: only a period (optional) + pagination. No tipo/estado/
 * search — the simple view surfaces exactly 3 movement types. */
export const QueryEstadoCuentaSimpleDtoSchema = z
  .object({
    fechaDesde: fechaISO.optional(),
    fechaHasta: fechaISO.optional(),
    limit: z.coerce.number().int().min(1).max(100).default(20),
    offset: z.coerce.number().int().min(0).default(0),
  })
  .refine(hasValidEstadoCuentaDateRange, ESTADO_CUENTA_DATE_RANGE_ERROR);

export type QueryEstadoCuentaSimpleDto = z.infer<typeof QueryEstadoCuentaSimpleDtoSchema>;

/** Export query deliberately omits pagination while retaining the screen filters. */
export const EstadoCuentaExportQuerySchema = EstadoCuentaFiltersSchema.extend({
  format: z.enum([ESTADO_CUENTA_EXPORT_FORMAT.PDF, ESTADO_CUENTA_EXPORT_FORMAT.XLSX]),
}).refine(hasValidEstadoCuentaDateRange, ESTADO_CUENTA_DATE_RANGE_ERROR);

export type EstadoCuentaExportQuery = z.infer<typeof EstadoCuentaExportQuerySchema>;
export type EstadoCuentaExportFilters = Omit<EstadoCuentaExportQuery, 'format'>;

/** Simple statement export: mirrors the on-screen simple view. Only a period
 * (optional) plus the output format — no tipo/estado/search filters. */
export const EstadoCuentaSimpleExportQuerySchema = z
  .object({
    format: z.enum([ESTADO_CUENTA_EXPORT_FORMAT.PDF, ESTADO_CUENTA_EXPORT_FORMAT.XLSX]),
    fechaDesde: fechaISO.optional(),
    fechaHasta: fechaISO.optional(),
  })
  .refine(hasValidEstadoCuentaDateRange, ESTADO_CUENTA_DATE_RANGE_ERROR);

export type EstadoCuentaSimpleExportQuery = z.infer<typeof EstadoCuentaSimpleExportQuerySchema>;
export type EstadoCuentaSimpleExportFilters = Omit<EstadoCuentaSimpleExportQuery, 'format'>;

/** Query parameters for the exact calendar-month financial-history projection. */
export const QueryEstadoCuentaSerieMensualDtoSchema = z.object({
  meses: z.coerce
    .number()
    .int()
    .min(1)
    .max(ESTADO_CUENTA_SERIE_MENSUAL.MAX_MONTHS)
    .default(ESTADO_CUENTA_SERIE_MENSUAL.DEFAULT_MONTHS),
});

export type QueryEstadoCuentaSerieMensualDto = z.infer<
  typeof QueryEstadoCuentaSerieMensualDtoSchema
>;

const deactivationOperationKey = z.string().trim().min(1).max(160);
const deactivationPreviewHash = z.string().regex(/^[a-f0-9]{64}$/i, 'previewHash must be SHA-256');

export const PreviewClientDeactivationDtoSchema = z.object({
  reason: z.string().trim().min(1).max(500),
  cutoffStrategy: z.enum([CUTOFF_STRATEGY.IMMEDIATE, CUTOFF_STRATEGY.END_OF_PERIOD]).optional(),
  currentPeriodTreatment: z
    .enum([
      CURRENT_PERIOD_TREATMENT.CHARGE_SERVED_DAYS,
      CURRENT_PERIOD_TREATMENT.FULL_PERIOD,
      CURRENT_PERIOD_TREATMENT.FORGIVE_CURRENT_PERIOD,
    ])
    .optional(),
  operationKey: deactivationOperationKey,
  effectiveDate: fechaISO.optional(),
});

export type PreviewClientDeactivationDto = z.infer<typeof PreviewClientDeactivationDtoSchema>;

export const ConfirmClientDeactivationDtoSchema = z.object({
  reason: z.string().trim().min(1).max(500),
  cutoffStrategy: z.enum([CUTOFF_STRATEGY.IMMEDIATE, CUTOFF_STRATEGY.END_OF_PERIOD]),
  currentPeriodTreatment: z.enum([
    CURRENT_PERIOD_TREATMENT.CHARGE_SERVED_DAYS,
    CURRENT_PERIOD_TREATMENT.FULL_PERIOD,
    CURRENT_PERIOD_TREATMENT.FORGIVE_CURRENT_PERIOD,
  ]),
  operationKey: deactivationOperationKey,
  effectiveDate: fechaISO,
  previewHash: deactivationPreviewHash,
});

export type ConfirmClientDeactivationDto = z.infer<typeof ConfirmClientDeactivationDtoSchema>;

// ---------------------------------------------------------------------------
// Billing-day change adjustment (cambio de día de facturación)
// ---------------------------------------------------------------------------

const cambioDiaOperationKey = z.string().trim().min(1).max(160);

/**
 * Direction of the optional adjustment when the billing day moves:
 * - CARGO: the new schedule leaves [fecha anterior, fecha nueva) uncovered.
 * - DESCUENTO: the new schedule double-covers [fecha nueva, fecha anterior).
 * - SIN_EFECTO: the date move does not change the billing period start.
 */
export const CAMBIO_DIA_FACTURACION_DIRECCION = {
  CARGO: 'CARGO',
  DESCUENTO: 'DESCUENTO',
  SIN_EFECTO: 'SIN_EFECTO',
} as const;

export type CambioDiaFacturacionDireccion =
  (typeof CAMBIO_DIA_FACTURACION_DIRECCION)[keyof typeof CAMBIO_DIA_FACTURACION_DIRECCION];

/** Why an adjustment is not applicable; null when `aplicable` is true. */
export const CAMBIO_DIA_FACTURACION_MOTIVO = {
  SIN_EFECTO: 'SIN_EFECTO',
  SIN_SERVICIOS: 'SIN_SERVICIOS',
  MONTO_CERO: 'MONTO_CERO',
  SIN_SALDO: 'SIN_SALDO',
} as const;

export type CambioDiaFacturacionMotivo =
  (typeof CAMBIO_DIA_FACTURACION_MOTIVO)[keyof typeof CAMBIO_DIA_FACTURACION_MOTIVO];

export const CambioDiaFacturacionPreviewDtoSchema = z.object({
  /** Candidate next billing date (the new cursor). */
  fechaCobro: fechaISO,
});

export type CambioDiaFacturacionPreviewDto = z.infer<typeof CambioDiaFacturacionPreviewDtoSchema>;

export const CambioDiaFacturacionDtoSchema = z.object({
  fechaCobro: fechaISO,
  /** Cursor the operator saw when previewing; 409 when the DB moved on. */
  fechaCobroAnterior: fechaISO,
  /** Opt-in: false normalizes the dates only, without financial artifacts. */
  aplicarAjuste: z.boolean().default(false),
  /** Caller-owned idempotency key; derived from the move when omitted. */
  operacionKey: cambioDiaOperationKey.optional(),
});

export type CambioDiaFacturacionDto = z.infer<typeof CambioDiaFacturacionDtoSchema>;

/** One prorated billable service inside the adjustment range. */
export interface CambioDiaFacturacionLinea {
  descripcion: string;
  /** Plan price at the old period, as a money string. */
  precioUnitario: string;
  /** Prorated amount for the adjusted days, as a money string. */
  subtotal: string;
}

export interface CambioDiaFacturacionPreviewResponse {
  aplicable: boolean;
  motivoNoAplicable: CambioDiaFacturacionMotivo | null;
  direccion: CambioDiaFacturacionDireccion;
  /** Persisted cursor before the move (YYYY-MM-DD). */
  fechaAnterior: string;
  diaAnterior: number;
  /** Candidate cursor (YYYY-MM-DD). */
  fechaNueva: string;
  diaNuevo: number;
  /** Period the old cursor would have opened: [inicio, fin). */
  periodoAnterior: { inicio: string; fin: string };
  /** Calendar days moved: |fechaNueva - fechaAnterior|. */
  diasAjuste: number;
  lineas: CambioDiaFacturacionLinea[];
  /** Sum of `lineas.subtotal`; '0.00' when there is nothing to adjust. */
  total: string;
}

/** Financial artifact created by an applied adjustment. */
export interface CambioDiaFacturacionAjuste {
  tipo: typeof CAMBIO_DIA_FACTURACION_DIRECCION.CARGO | typeof CAMBIO_DIA_FACTURACION_DIRECCION.DESCUENTO;
  /** Invoice id (CARGO) or exception id (DESCUENTO). */
  id: number;
  /** Target invoice for DESCUENTO; null for CARGO. */
  idCobro: number | null;
  monto: string;
  concepto: string;
  /** True when the operation key already produced this artifact. */
  idempotente: boolean;
}

export interface CambioDiaFacturacionResponse {
  /** Client after normalizing the billing dates. */
  cliente: import('./types').ClienteResponse;
  /** Null when `aplicarAjuste` was false or the move had no effect. */
  ajuste: CambioDiaFacturacionAjuste | null;
}
